import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Role } from "services/rbac/types";
import { useRbacRoles } from "services/rbac/useRbacRoles";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { usePrivilegesState } from "../usePrivilegesContext";
import { formatPrivilege } from "../utils";
import { GrantPrivilegesSelector } from "./GrantPrivilegesSelector";
import { GroupInner } from "./GroupInner";
import { ALL_ROLES } from "./constant";
import { usePrivilegesHandlers } from "./usePrivilegesHandlers";
import { getOtherGroupsObjects } from "./utils";

import styles from "./styles.module.scss";

const privileges = ["MODIFY"];

type Props = {
  index: number;
  resource: string;
};

export const RolesGroup = (props: Props) => {
  const { index, resource } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();
  const menu = useMenu();
  const roles = useRbacRoles();

  const isViewOnly = useViewOnly();
  const { t } = useTranslation();

  const {
    onGrantPrivileges,
    onSelectResourceObjects,
    deleteGroup,
    handleMore,
  } = usePrivilegesHandlers({ index, resource });

  const groupState = privilegesState.role[index];

  const otherGroupsObjects = getOtherGroupsObjects(privilegesState.role, index);

  const { objects, expanded } = groupState;

  const dependencies = useRef({
    index,
    privilegesDispatch,
    resource,
    objects,
  });

  useEffect(() => {
    const { index, privilegesDispatch, resource, objects } =
      dependencies.current;
    if (!objects.length) {
      privilegesDispatch({
        type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
        resource,
        objects: [ALL_ROLES],
        index,
      });
    }
  }, [dependencies]);

  const menuItems = [
    [
      ...(objects.includes(ALL_ROLES)
        ? []
        : [
            {
              key: "add_remaining",
              text: t("roles_wizard.privileges.menu.add_remaining_roles"),
              action: () => {
                privilegesDispatch({
                  type: ROLES_WIZARD_ADD_GROUP_WITH_REMAINING_OBJECTS,
                  resource,
                  allObjects: roles.map(({ roleName }) => roleName),
                });
              },
              testId: "roles-wizard-add-remaining-roles",
              disabled: isViewOnly,
            },
          ]),
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [ALL_ROLES],
          });
        },
        testId: "roles-wizard-add-roles-group",
        disabled: isViewOnly,
      },
    ],

    ...(privilegesState.role.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-roles-group",
              disabled: isViewOnly,
            },
          ],
        ]
      : []),
  ];

  const prefixItems = [
    <ContextMenuItem
      disabled={isViewOnly}
      value={ALL_ROLES}
      key={ALL_ROLES}
      checked={objects.includes(ALL_ROLES)}
      checkedIconPlaceholder={true}
      text={t("roles_wizard.privileges.all_roles")}
      direction="left"
      bulkItem
      skipFilter
      onClick={() =>
        onSelectResourceObjects(objects.includes(ALL_ROLES) ? [] : [ALL_ROLES])
      }
    />,
    <ContextMenuDivider key="divider" />,
  ];

  const renderItem = (role: Role) => {
    const { roleName } = role;
    const disabled = otherGroupsObjects[roleName];
    const disabledTooltip =
      disabled && !isViewOnly
        ? t("roles_wizard.privileges.disabled_role_tooltip", {
            role: roleName,
          })
        : undefined;
    return (
      <ContextMenuItem
        value={roleName}
        key={roleName}
        checked={objects.includes(roleName)}
        disabled={disabled || isViewOnly}
        disabledTooltip={disabledTooltip}
        checkedIconPlaceholder={true}
        text={roleName}
        direction="left"
        onClick={() => {
          objects.includes(roleName)
            ? onSelectResourceObjects(
                objects
                  .filter(obj => obj !== roleName)
                  .filter(obj => obj !== ALL_ROLES)
              )
            : onSelectResourceObjects(
                [...objects, roleName].filter(obj => obj !== ALL_ROLES)
              );
        }}
      />
    );
  };

  const privilegesOptions = privileges.map(privilege => {
    return {
      value: privilege,
      text: formatPrivilege(privilege),
    };
  });

  return (
    <div className={styles.group}>
      <GroupInner
        handleMore={handleMore}
        expanded={expanded}
        objects={objects}
        prefixItems={prefixItems}
        items={roles}
        resource={resource}
        renderItem={renderItem}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_account_role")}
        searchKey="roleName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
